/**
 * downloadFromUrl — trigger a browser "save file" for an authenticated
 * endpoint. Shared so every credentialed file download (PDF reports, the
 * repo `.zip`, …) uses the same fetch → blob → `<a download>` flow and the
 * same `Content-Disposition` filename handling, rather than each button
 * re-implementing (and drifting on) error + filename behavior.
 *
 * Extracted from `DownloadPdfButton` when the repo-download icon (#2055)
 * needed the identical behavior with a different glyph. `DownloadPdfButton`
 * still carries its own inline copy; a later pass can route it through here
 * too (TODO — behavior is intentionally identical).
 */

/** The server's suggested filename from `Content-Disposition`, or null. */
export function filenameFromResponse(res: Response): string | null {
  const cd = res.headers.get('content-disposition')
  if (!cd) return null
  const m = /filename="([^"]+)"/.exec(cd)
  return m?.[1] ?? null
}

/** A non-2xx download. Carries the HTTP status so a caller can tell an
 * expected refusal (409: nothing to download yet) from a real failure. */
export class DownloadError extends Error {
  constructor(message: string, readonly status: number) {
    super(message)
    this.name = 'DownloadError'
  }
}

/**
 * Fetch `url` with credentials and save the body as a file. Throws a
 * {@link DownloadError} with the server's `message`/`error` and the status
 * on a non-2xx so callers can surface the precise reason (e.g. "Repository
 * not ready"). Uses the response's
 * `Content-Disposition` filename, falling back to `fallbackFilename`.
 */
export async function downloadFromUrl(
  url: string,
  fallbackFilename: string,
): Promise<void> {
  const res = await fetch(url, { credentials: 'include' })
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      message?: string
      error?: string
    }
    throw new DownloadError(body?.message || body?.error || `Download failed (${res.status})`, res.status)
  }
  const blob = await res.blob()
  const blobUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = filenameFromResponse(res) ?? fallbackFilename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(blobUrl)
}
